import {
  AfterViewInit, ChangeDetectionStrategy, Component, computed, ElementRef, HostBinding,
  input, output, effect, viewChild, signal, model
} from '@angular/core';

import { YouTubePlayer } from '@angular/youtube-player';
import { TranslateModule } from '@ngx-translate/core';
import { NgTemplateOutlet } from '@angular/common';

import { SdMediaImageComponent } from '../media-image/media-image.component';
import { SdMediaVideoComponent } from '../media-video/media-video.component';
import { SdModalDefaultComponent } from '../../modals/modal-default/modal-default.component';
import { ImageRatioEnum } from '../../../_enums/image-ratio.enum';
import { IMedia } from '../../../_interfaces/media.interface';
import { PositionEnum } from '../../../_enums/position.enum';
import { SdButtonComponent } from '../../buttons/button/button.component';
import { SdIconComponent } from '../../icons/icon/icon.component';
import { ThemesEnum } from '../../../_enums/themes.enum';
import { SdSkeletonComponent } from '../../skeleton/skeleton.component';
import { SkeletonAppearanceEnum } from '../../../_enums/skeleton.enum';

@Component({
  selector: 'sd-banner',
  templateUrl: './banner.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SdButtonComponent,
    SdIconComponent,
    SdMediaVideoComponent,
    SdMediaImageComponent,
    NgTemplateOutlet,
    SdModalDefaultComponent,
    SdSkeletonComponent,
    TranslateModule
  ]
})
export class SdBannerComponent implements AfterViewInit {
  videoTitle = input('');
  isComponentLoading = input(false);
  width = input<number>();
  height = input<number>();
  media = input.required<IMedia>();
  buttonPosition = input<PositionEnum.left | PositionEnum.right>();
  isButtonSmall = input(false);
  customId = input('');
  mediaTitle = input('');
  controlsVideo = input<string>();
  loopVideo = input(false);
  muted = input(true);
  autoplayVideo = input(false);
  isVideoThumbnail = input(false);
  imgRatio = input<ImageRatioEnum>();
  hasBorder = input(false);
  hasAltText = input(true);
  isAriaHidden = input(false);
  twicPicsAutoSize = input(true);
  play = model(false);
  showPlayButton = input(true);
  hasModal = input(false);
  isFit = input(false);
  isZoomed = input(false);
  zoomLevel = input(0);
  class = input('');

  mediaStatus = output<boolean>();
  mediaPlay = output<void>();
  mediaEnded = output<void>();
  imageIsLoaded = output<void>();

  @HostBinding('class')
  get hostClasses(): string {
    return this.computedHostClasses();
  }

  mediaVideo = viewChild<SdMediaVideoComponent>('mediaVideo');
  mediaImage = viewChild<SdMediaImageComponent>('mediaImage');

  // ATTENTION!! : the next three public vars can be externally accessed
  targetMedia?: ElementRef<any>;
  youtubePlayer: YouTubePlayer;

  smallButton = signal(this.isButtonSmall());
  mediaTypeClass = signal<string>('');
  isYoutube = signal(false);
  isGif = signal(false);
  isVideo = signal(false);
  isOpen = signal(false);

  SkeletonAppearanceEnum = SkeletonAppearanceEnum;
  ImageRatioEnum = ImageRatioEnum;
  ThemeEnum = ThemesEnum;

  computedHostClasses = computed(() => {
    const btnPosition = this.buttonPosition();
    return [
      this.class(),
      this.mediaTypeClass(),
      this.controlsVideo() || this.isYoutube() ? 'has-control' : '',
      this.isYoutube() ? 'is-youtube' : '',
      this.media()?.urlTwicPic ? 'is-twicpics' : '',
      btnPosition
        ? 'has-button-' + btnPosition
        : this.smallButton()
          ? 'has-button-' + PositionEnum.right
          : null,
      this.smallButton() ? 'has-small-button' : ''
    ]
      .filter(Boolean)
      .join(' ');
  });

  title = computed(() => {
    return this.mediaTitle() || this.media().altText || '';
  });

  constructor() {
    effect(() => {
      const media = this.media();
      if (media) {
        if (media.isVideo === true) {
          this.isYoutube.set(media.videoId !== undefined);
          this.isVideo.set(true);
        } else if (media.mime?.includes('gif')) {
          this.smallButton.set(true);
          this.isGif.set(true);
          this.play.set(true);
        }
      }
    });

    effect(() => {
      if (this.isVideo() || this.isGif()) {
        const play = this.play();
        this.mediaStatus.emit(play);
        if (play) {
          this.mediaPlay.emit();
          this.playMedia();
        } else {
          this.mediaEnded.emit();
          this.pauseMedia();
        }
      }
    });
  }

  ngAfterViewInit() {
    if (this.isVideo()) {
      if (this.isYoutube()) {
        this.youtubePlayer = this.mediaVideo()?.youtubePlayer();
      } else {
        this.targetMedia = this.mediaVideo()?.targetMedia();
      }
    } else {
      this.targetMedia = this.mediaImage()?.targetMedia();
    }
  }

  togglePlay(): void {
    this.play.set(!this.play());
  }

  playMedia(): void {
    this.play.set(true);
  }

  pauseMedia(): void {
    this.play.set(false);
  }

  toggleAction(isModal: boolean): void {
    const hasModal = this.hasModal();
    if ((isModal && hasModal) || this.isGif() || !hasModal) {
      this.togglePlay();
    } else if (!isModal && hasModal) {
      this.toggleModal(true);
    }
  }

  toggleModal(open: boolean): void {
    this.isOpen.set(open);
    this.play.set(open);
  }

  onMediaEnded(): void {
    this.mediaStatus.emit(false);
    this.mediaEnded.emit();
  }

  imageLoaded(): void {
    this.imageIsLoaded.emit();
  }
}
